import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Network,
  ArrowRight,
  Key,
  ClipboardCheck,
  CheckCircle2,
  BookOpen,
  Mail,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const services = [
  {
    Icon: Network,
    title: "BACnet/SC Readiness Assessment",
    text: "Standortbestimmung Ihrer bestehenden BACnet/IP Landschaft: Geräte, Firmware, Netzwerkstruktur, Fernzugriffe und welche Anlagenteile zuerst auf BACnet/SC umgestellt werden sollten.",
  },
  {
    Icon: ArrowRight,
    title: "Migrationsplanung und Roadmap",
    text: "Priorisierte Umstellungsstrategie von BACnet/IP auf BACnet/SC, phasenweise nach Gebäude, Trakt oder Anlagenteil, mit klaren Meilensteinen und Rollback-Optionen.",
  },
  {
    Icon: Key,
    title: "PKI und Zertifikatskonzept",
    text: "Konzept für Certificate Signing Requests, Zertifikats-Lebenszyklus, Key-Management und Integration in Ihre bestehende IT-Sicherheitsinfrastruktur, ohne den GA-Betrieb zu gefährden.",
  },
  {
    Icon: ClipboardCheck,
    title: "Implementierung, Test und Abnahme",
    text: "Begleitung bei der Konfiguration von BACnet/SC Hub, Router und Endgeräten, Funktions- und Sicherheitstests, Dokumentation und Abnahme mit Facility, Integrator und IT.",
  },
];

const benefits = [
  "Sie ersetzen unsichere BACnet/IP Broadcasts und offene Fernzugriffe durch verschlüsselte, zertifikatsbasierte Kommunikation.",
  "Sie behalten Ihre Investition in bestehende BACnet-Geräte, weil BACnet/SC auf der bewährten BACnet-Applikationsschicht aufsetzt.",
  "Sie schaffen die technische Basis, um NIS-2, IEC 62443 und BSI INF.13 für Gebäudeautomation wirtschaftlich zu erfüllen.",
  "Sie reduzieren das Risiko unautorisierter Zugriffe, Manipulation und Ausfälle in der Gebäudeautomation.",
  "Sie bekommen einen nachvollziehbaren Migrationsplan, den Betreiber, Integrator und IT gemeinsam umsetzen können.",
];

const faqs = [
  {
    q: "Was ist BACnet/SC?",
    a: "BACnet/SC (Secure Connect) ist die sichere Weiterentwicklung von BACnet/IP. Statt Broadcasts und offener UDP-Kommunikation nutzt BACnet/SC verschlüsselte WebSocket-Verbindungen und ein PKI-basiertes Zertifikatsmodell. Die BACnet-Applikation bleibt erhalten, die Übertragung wird abgesichert.",
  },
  {
    q: "Warum sollte ich von BACnet/IP auf BACnet/SC migrieren?",
    a: "Klassisches BACnet/IP setzt auf Broadcasts und bietet keine Authentifizierung oder Verschlüsselung. Mit steigenden Cybersecurity-Anforderungen durch NIS-2, IEC 62443 und BSI Grundschutz reicht das für viele Liegenschaften nicht mehr aus. BACnet/SC schließt diese Lücken, ohne die Gerätewelt zu ersetzen.",
  },
  {
    q: "Müssen alle Geräte auf BACnet/SC umgestellt werden?",
    a: "Nein. BACnet/SC kann schrittweise eingeführt werden. Bestehende BACnet/IP-Geräte lassen sich über BACnet/SC-Router oder -Hubs anbinden. Wir planen die Migration so, dass kritische Zonen und Fernzugriffe zuerst abgesichert werden und der Betrieb während der Umstellung läuft.",
  },
  {
    q: "Wie hängt BACnet/SC mit IEC 62443 und NIS-2 zusammen?",
    a: "BACnet/SC ist ein wichtiger technischer Baustein, um die Anforderungen an sichere Kommunikation, Segmentierung und Fernzugriff zu erfüllen. IEC 62443 liefert das übergeordnete Betriebsmodell, NIS-2 die regulatorische Pflicht. Ich mappe diese Ebenen, damit die Migration nicht nur technisch, sondern auch prüfbar ist.",
  },
  {
    q: "Was ist ein BACnet/SC Hub?",
    a: "Der BACnet/SC Hub ersetzt den Broadcast-Mechanismus von BACnet/IP durch eine zentrale, zertifikatsbasierte Verteilung von Nachrichten. Er kann lokal, in der Cloud oder bei einem Dienstleister betrieben werden. Im Konzept klären wir Betriebsmodell, Verantwortlichkeit und Ausfallszenarien.",
  },
  {
    q: "Wie starten wir die Migration?",
    a: "Der pragmatische Einstieg ist ein BACnet/SC Readiness Assessment. Wir erfassen Ihre bestehende Landschaft, identifizieren Quick Wins und erstellen eine priorisierte Roadmap. Alternativ starten Sie mit dem FACHSPRECHPARTNER Workbook und einem 60-minütigen Sparring.",
  },
];

const BacnetSc = () => {
  const openContact = () =>
    window.dispatchEvent(new CustomEvent("open-contact-form"));

  const canonical = "https://fachsprechpartner.de/bacnet-sc-migration";

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: "BACnet/SC Migration für Gebäudeautomation",
        serviceType: "OT-Security und BACnet/SC Beratung",
        provider: {
          "@type": "Organization",
          name: "FACHSPRECHPARTNER",
          url: "https://fachsprechpartner.de",
        },
        areaServed: { "@type": "Country", name: "Deutschland" },
        description:
          "Beratung zur Migration von BACnet/IP auf BACnet/SC: Readiness Assessment, Migrationsplanung, PKI- und Zertifikatskonzept sowie Implementierung, Test und Abnahme.",
        url: canonical,
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fachsprechpartner.de/" },
          { "@type": "ListItem", position: 2, name: "BACnet/SC Migration", item: canonical },
        ],
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>BACnet/SC Migration: Sichere Gebäudeautomation</title>
        <meta
          name="description"
          content="Migration von BACnet/IP auf BACnet/SC: Readiness Assessment, Migrationsplanung, PKI/Zertifikate und Implementierung für sichere Gebäudeautomation nach IEC 62443 und NIS-2."
        />
        <meta
          name="keywords"
          content="BACnet/SC, BACnet SC Migration, BACnet/IP, BACnet Secure Connect, Gebäudeautomation, OT-Security, PKI, Zertifikate, NIS-2, IEC 62443"
        />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="de" href={canonical} />
        <link rel="alternate" hrefLang="x-default" href={canonical} />
        <meta property="og:title" content="BACnet/SC Migration: Sichere Gebäudeautomation" />
        <meta
          property="og:description"
          content="Migration von BACnet/IP auf BACnet/SC: Assessment, Roadmap, PKI und Implementierung für sichere Gebäudeautomation."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content="https://fachsprechpartner.de/og-image.webp" />
        <meta property="og:image:type" content="image/webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="FACHSPRECHPARTNER Tim Bauer, OT-Security und Gebäudeautomation" />
        <meta property="og:locale" content="de_DE" />
        <meta property="og:site_name" content="Fachsprechpartner" />
        <meta name="twitter:title" content="BACnet/SC Migration: Sichere Gebäudeautomation" />
        <meta name="twitter:description" content="Migration von BACnet/IP auf BACnet/SC: Assessment, Roadmap, PKI und Implementierung für sichere Gebäudeautomation." />
        <meta name="twitter:image" content="https://fachsprechpartner.de/og-image.webp" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Navbar />

      <main className="min-h-screen bg-background">
        {/* Hero */}
        <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-b from-primary to-primary/90 text-primary-foreground">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 text-accent text-xs font-semibold uppercase tracking-wider mb-6">
                <ShieldCheck className="w-4 h-4" />
                BACnet/SC · Secure Connect
              </div>
              <h1 className="text-4xl md:text-6xl font-heading font-bold leading-tight mb-6">
                BACnet/SC Migration: Ihre Gebäudeautomation absichern.
              </h1>
              <p className="text-lg md:text-xl text-primary-foreground/85 mb-8 leading-relaxed max-w-3xl">
                Ich begleite Sie von BACnet/IP zu BACnet/SC: vom Readiness
                Assessment über Migrationsplanung und PKI-Konzept bis zur
                Implementierung und Abnahme. Pragmatisch, betriebssicher und
                abgestimmt auf IEC 62443 sowie NIS-2.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={openContact}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-colors"
                >
                  Assessment anfragen <ArrowRight className="w-4 h-4" />
                </button>
                <Link
                  to="/workbook"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-primary-foreground/30 text-primary-foreground font-semibold hover:bg-primary-foreground/10 transition-colors"
                >
                  <BookOpen className="w-4 h-4" />
                  Zum Workbook
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Leistungen */}
        <section className="py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Leistungen rund um BACnet/SC
            </h2>
            <p className="text-muted-foreground mb-12 max-w-2xl">
              Vier Bausteine, die Ihre Migration von BACnet/IP auf BACnet/SC
              strukturiert und betriebssicher machen, von der Analyse bis zur
              Abnahme.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {services.map(({ Icon, title, text }) => (
                <div
                  key={title}
                  className="p-6 rounded-2xl border border-border bg-card hover:shadow-lg transition-shadow"
                >
                  <div className="w-12 h-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold mb-2">
                    {title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Nutzen */}
        <section className="py-16 md:py-24 bg-muted/40">
          <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                Was Sie davon haben
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                BACnet/SC ist mehr als ein Protokollupdate. Richtig eingeführt,
                wird es zum technischen Fundament für sicheren Betrieb,
                regulatorische Konformität und vertrauensvolle Zusammenarbeit
                zwischen Facility, Integrator und IT.
              </p>
            </div>
            <ul className="space-y-3">
              {benefits.map((b) => (
                <li key={b} className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-1" />
                  <span className="text-foreground/90">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-8">
              Häufige Fragen zu BACnet/SC
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`}>
                  <AccordionTrigger className="text-left font-semibold">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 bg-primary text-primary-foreground">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Starten Sie die BACnet/SC Migration
            </h2>
            <p className="text-primary-foreground/85 mb-8 leading-relaxed">
              Beginnen Sie mit einem Readiness Assessment oder dem Workbook. In
              beiden Fällen erhalten Sie einen klaren, umsetzbaren Fahrplan für
              sichere Gebäudeautomation.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/workbook"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-colors"
              >
                <BookOpen className="w-4 h-4" />
                Workbook ansehen
              </Link>
              <button
                onClick={openContact}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-primary-foreground/30 font-semibold hover:bg-primary-foreground/10 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Sparring anfragen
              </button>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default BacnetSc;
